import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from './analytics';
import { consentAwareAnalytics } from './consentAwareAnalytics';

interface AnalyticsContextType {
  trackPageView: (path: string, title?: string, additionalData?: Record<string, any>) => void;
  trackEvent: (action: string, category: string, label?: string, value?: number) => void;
  trackBlogEvent: (action: string, postSlug?: string, category?: string) => void;
  trackCourseEvent: (action: string, courseId?: string, category?: string) => void;
  trackContactEvent: (action: string, formType?: string) => void;
  trackConsentEvent: (action: string, consentType: string, granted: boolean) => void;
  trackRegistrationEvent: (action: string, courseId: string, consentData: { dataProcessing: boolean; marketing: boolean }, registrationId?: string) => void;
  trackMarketingEvent: (action: string, category: string, label?: string, value?: number) => void;
  updateConsentPreferences: (preferences: { analytics: boolean; marketing: boolean; functional: boolean }) => Promise<void>;
  isReady: () => boolean;
  hasAnalyticsConsent: () => boolean;
  hasMarketingConsent: () => boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  const [isConsentAwareInitialized, setIsConsentAwareInitialized] = useState(false);

  // Initialize both analytics services on mount
  useEffect(() => {
    const initializeAnalytics = async () => {
      // Initialize legacy analytics for backward compatibility
      analytics.initialize();
      
      // Initialize consent-aware analytics
      await consentAwareAnalytics.initialize();
      setIsConsentAwareInitialized(true);
    };

    initializeAnalytics();
  }, []);

  // Track page views on route changes with consent awareness
  useEffect(() => {
    if (isConsentAwareInitialized) {
      const pageTitle = document.title;
      const path = location.pathname + location.search;
      
      // Add special tracking for blog pages
      if (location.pathname.startsWith('/blog')) {
        consentAwareAnalytics.trackPageView(path, pageTitle, {
          content_type: 'blog',
          blog_section: location.pathname === '/blog' ? 'blog_listing' : 'blog_post'
        });
      } else {
        consentAwareAnalytics.trackPageView(path, pageTitle);
      }
    }
  }, [location, isConsentAwareInitialized]);

  const contextValue: AnalyticsContextType = {
    trackPageView: consentAwareAnalytics.trackPageView.bind(consentAwareAnalytics),
    trackEvent: consentAwareAnalytics.trackEvent.bind(consentAwareAnalytics),
    trackBlogEvent: consentAwareAnalytics.trackBlogEvent.bind(consentAwareAnalytics),
    trackCourseEvent: consentAwareAnalytics.trackCourseEvent.bind(consentAwareAnalytics),
    trackContactEvent: consentAwareAnalytics.trackContactEvent.bind(consentAwareAnalytics),
    trackConsentEvent: consentAwareAnalytics.trackConsentEvent.bind(consentAwareAnalytics),
    trackRegistrationEvent: consentAwareAnalytics.trackRegistrationEvent.bind(consentAwareAnalytics),
    trackMarketingEvent: consentAwareAnalytics.trackMarketingEvent.bind(consentAwareAnalytics),
    updateConsentPreferences: consentAwareAnalytics.updateConsentPreferences.bind(consentAwareAnalytics),
    isReady: consentAwareAnalytics.isReady.bind(consentAwareAnalytics),
    hasAnalyticsConsent: () => {
      const preferences = consentAwareAnalytics.getConsentPreferences();
      return preferences?.analytics || false;
    },
    hasMarketingConsent: () => {
      const preferences = consentAwareAnalytics.getConsentPreferences();
      return preferences?.marketing || false;
    },
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

// Custom hook to use analytics
export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}

// Component to handle route change tracking (alternative approach)
export function RouteChangeTracker() {
  const location = useLocation();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    const pageTitle = document.title;
    const path = location.pathname + location.search;
    
    // Track page view with additional parameters for blog pages
    if (location.pathname.startsWith('/blog')) {
      trackPageView(path, pageTitle, {
        content_type: 'blog',
        blog_section: location.pathname === '/blog' ? 'blog_listing' : 'blog_post'
      });
    } else {
      trackPageView(path, pageTitle);
    }
  }, [location, trackPageView]);

  return null; // This component doesn't render anything
}